import React, { useState, type FC } from 'react'
import { t } from 'i18next'
import { capitalize, isEmpty } from 'lodash'
import { format } from 'date-fns'
import ReactJson from 'react-json-view'
import {
  Table,
  TableBody,
  TableHeader,
  TableHead,
  TableRow,
  TableCell,
  Button,
  Badge,
  AlertDialog
} from '@awell-health/design-system'
import { custom_json_parser } from '@awell/ui-kit/utils'
import { useCopyToClipboard } from '../../../hooks/useCopyToClipboard'
import { type ApiCallResponse } from '../../../generated/types-orchestration'

interface ApiCallResponsesTableProps {
  responses: Array<ApiCallResponse>
}

const getStatusBadgeVariant = (status: number): string => {
  if (status >= 100 && status <= 199) {
    return 'primary'
  }
  if (status >= 200 && status <= 299) {
    return 'success'
  }
  if (status >= 300 && status <= 399) {
    return 'violet'
  }
  if (status >= 400 && status <= 499) {
    return 'error'
  }
  return 'warning'
}

export const ApiCallResponsesTable: FC<ApiCallResponsesTableProps> = ({ responses }) => {
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedResponse, setSelectedResponse] = useState<ApiCallResponse | null>(null)
  const { copyToClipboard } = useCopyToClipboard()

  const handleViewResponse = (response: ApiCallResponse) => {
    setSelectedResponse(response)
    setModalOpen(true)
  }

  const handleCopyToClipboard = () => {
    if (selectedResponse?.body) {
      copyToClipboard(selectedResponse.body)
      setModalOpen(false)
    }
  }

  const handleCloseModal = () => {
    setModalOpen(false)
    setSelectedResponse(null)
  }

  const columns = [
    { title: t('called_at'), field: 'timestamp' },
    { title: t('status'), field: 'status' },
    { title: capitalize(t('action_other')), field: 'action' }
  ]

  const jsonBody = selectedResponse ? custom_json_parser(selectedResponse.body) : null

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map(({ field, title }) => (
              <TableHead key={field} sortable={false}>
                {title}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {responses.length > 0 ? (
            responses.map((response, index) => (
              <TableRow key={index}>
                <TableCell>
                  {isEmpty(response?.date) ? '--' : format(new Date(response.date), 'd-LLL-yyyy K:mm:ss a')}
                </TableCell>
                <TableCell>
                  <Badge variant={getStatusBadgeVariant(response.status)}>{response.status}</Badge>
                </TableCell>
                <TableCell>
                  <Button variant="ghost" size="sm" onClick={() => handleViewResponse(response)}>
                    {t('view_response')}
                  </Button>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={3} className="text-center">
                No responses
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {modalOpen && (
        <AlertDialog
          onClose={handleCloseModal}
          title={t('response_body')}
          button={{
            label: t('copy_to_clipboard'),
            buttonAction: handleCopyToClipboard,
            buttonVariant: 'primary'
          }}
          secondaryButton={{
            label: t('close'),
            buttonAction: handleCloseModal
          }}
        >
          <div className="bg-slate-100 rounded-md p-4 max-h-[250px] overflow-auto">
            {typeof jsonBody === 'string' ? (
              <p>{t('unavailable_response')}</p>
            ) : (
              <ReactJson src={jsonBody} enableClipboard={false} />
            )}
          </div>
        </AlertDialog>
      )}
    </>
  )
}
